/**
 * Keuzelijst in de huisstijl; nooit de lijst van het besturingssysteem.
 *
 * Te gebruiken zoals een `<select>`: dezelfde `value`, `onChange` met
 * `e.target.value`, en `<option>`- en `<optgroup>`-kinderen. Het paneel staat in de
 * top layer (popover), zodat een paneel met `overflow: hidden` of een dialoog het niet
 * afsnijdt, en opent omhoog als er onder te weinig ruimte is. Pijltjes, Home en End
 * bewegen, Enter of spatie kiest, Escape of klikken ernaast sluit, een letter springt
 * naar de volgende optie die ermee begint.
 *
 * Overgenomen uit het 3D Print Portal (`SelectField`), zonder Tailwind.
 */
import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type OptionHTMLAttributes,
  type ReactNode,
} from 'react';

import { IconChevronOnder, IconVink } from './Iconen';

interface Optie {
  value: string;
  label: string;
  disabled: boolean;
  groep?: string;
}

const tekstVan = (n: ReactNode): string => Children.toArray(n).join('');

/** De `<option>`-kinderen als lijst, ook binnen een fragment of `<optgroup>`. */
function optiesUit(children: ReactNode, groep?: string): Optie[] {
  return Children.toArray(children).flatMap((kind): Optie[] => {
    if (!isValidElement<{ children?: ReactNode; label?: string }>(kind)) return [];
    if (kind.type === Fragment) return optiesUit(kind.props.children, groep);
    if (kind.type === 'optgroup') return optiesUit(kind.props.children, kind.props.label);
    if (kind.type !== 'option') return [];
    const p = kind.props as OptionHTMLAttributes<HTMLOptionElement>;
    const label = tekstVan(p.children);
    return [{ value: p.value === undefined ? label : String(p.value), label, disabled: !!p.disabled, groep }];
  });
}

/** Onder de knop, of erboven als daar meer ruimte is en onder te weinig. */
function paneelPlek(knop: DOMRect): CSSProperties {
  const venster = window.innerHeight;
  const onder = venster - knop.bottom - 8;
  const boven = knop.top - 8;
  const omhoog = onder < 240 && boven > onder;
  return {
    left: knop.left,
    minWidth: knop.width,
    maxHeight: Math.min(320, omhoog ? boven : onder) - 4,
    top: omhoog ? 'auto' : knop.bottom + 4,
    bottom: omhoog ? venster - knop.top + 4 : 'auto',
  };
}

interface Props {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  children: ReactNode;
}

export function Keuzelijst({ value, onChange, disabled, className, style, children, ...rest }: Props) {
  const opties = optiesUit(children);
  const knop = useRef<HTMLButtonElement>(null);
  const paneel = useRef<HTMLUListElement>(null);
  const lijstId = useId();
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(0);
  const [plek, setPlek] = useState<CSSProperties>({});
  const gekozen = opties.find((o) => o.value === value);

  // De popover-attributen en -gebeurtenissen kent React 18 nog niet; die gaan via de DOM.
  useEffect(() => {
    const el = paneel.current;
    if (!el) return;
    el.setAttribute('popover', 'auto');
    const voor = (e: Event) => {
      if ((e as ToggleEvent).newState !== 'open') return setOpen(false);
      if (knop.current) setPlek(paneelPlek(knop.current.getBoundingClientRect()));
      setOpen(true);
      setActief(Math.max(0, opties.findIndex((o) => o.value === value)));
    };
    el.addEventListener('beforetoggle', voor);
    return () => el.removeEventListener('beforetoggle', voor);
  });

  // Meebewegen met scrollen en een ander venster zolang de lijst open is.
  useEffect(() => {
    const el = knop.current;
    if (!open || !el) return;
    const plaats = () => setPlek(paneelPlek(el.getBoundingClientRect()));
    window.addEventListener('resize', plaats);
    window.addEventListener('scroll', plaats, true);
    return () => {
      window.removeEventListener('resize', plaats);
      window.removeEventListener('scroll', plaats, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) paneel.current?.querySelector(`[data-index="${actief}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, actief]);

  const toon = () => {
    if (!paneel.current?.matches(':popover-open')) paneel.current?.showPopover();
  };
  const verberg = () => {
    if (paneel.current?.matches(':popover-open')) paneel.current.hidePopover();
  };

  const kies = (o: Optie | undefined) => {
    if (!o || o.disabled) return;
    if (o.value !== value) onChange({ target: { value: o.value } });
    verberg();
  };

  const toets = (e: KeyboardEvent<HTMLButtonElement>) => {
    const laatste = opties.length - 1;
    const naar = (i: number) => {
      setActief(Math.max(0, Math.min(laatste, i)));
      toon();
    };
    if (e.key === 'ArrowDown') naar(open ? actief + 1 : actief);
    else if (e.key === 'ArrowUp') naar(open ? actief - 1 : actief);
    else if (e.key === 'Home') naar(0);
    else if (e.key === 'End') naar(laatste);
    else if (open && (e.key === 'Enter' || e.key === ' ')) kies(opties[actief]);
    else if (e.key === 'Tab') return verberg();
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const letter = e.key.toLowerCase();
      const volgorde = [...opties.slice(actief + 1), ...opties.slice(0, actief + 1)];
      const raak = volgorde.find((o) => !o.disabled && o.label.toLowerCase().startsWith(letter));
      if (raak) naar(opties.indexOf(raak));
    } else return;
    e.preventDefault();
  };

  return (
    <span className="hs-keuzelijst" style={style}>
      <button
        ref={knop}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={lijstId}
        aria-activedescendant={open ? `${lijstId}-${actief}` : undefined}
        aria-label={rest['aria-label']}
        disabled={disabled}
        onClick={() => (open ? verberg() : toon())}
        onKeyDown={toets}
        className={`hs-keuzelijst__knop ${className ?? ''}`}
      >
        <span className="hs-keuzelijst__waarde">{gekozen?.label ?? opties[0]?.label}</span>
        <IconChevronOnder className={`hs-keuzelijst__pijl${open ? ' open' : ''}`} />
      </button>
      <ul
        ref={paneel}
        id={lijstId}
        role="listbox"
        aria-label={rest['aria-label']}
        // De focus blijft op de knop, ook bij klikken in de lijst.
        onMouseDown={(e) => e.preventDefault()}
        className="hs-keuzelijst__paneel"
        style={plek}
      >
        {opties.map((o, i) => (
          <Fragment key={`${o.value}-${i}`}>
            {o.groep && o.groep !== opties[i - 1]?.groep ? (
              <li role="presentation" className="hs-keuzelijst__groep">
                {o.groep}
              </li>
            ) : null}
            <li
              id={`${lijstId}-${i}`}
              data-index={i}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled || undefined}
              onPointerMove={() => setActief(i)}
              onClick={() => kies(o)}
              className={[
                'hs-keuzelijst__optie',
                i === actief ? 'actief' : '',
                o.value === value ? 'gekozen' : '',
                o.disabled ? 'uit' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span>{o.label}</span>
              {o.value === value ? <IconVink /> : null}
            </li>
          </Fragment>
        ))}
      </ul>
    </span>
  );
}
